import React, { Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, MapPin, ArrowUpRight } from 'lucide-react';
import { useChapterScene } from '../context/SceneContext';

const EarthGlobe = lazy(() => import('../components/three/EarthGlobe'));

const GlobeFallback = () => (
  <div className="w-full h-full flex items-center justify-center" data-testid="globe-fallback">
    <div className="font-mono text-[11px] tracking-[0.32em] uppercase text-white/30">
      <span style={{ color: 'var(--scene-accent)' }}>●</span> initialising signal hub…
    </div>
  </div>
);

const contactLinks = [
  {
    name: 'Email',
    value: 'okaforkingsley777@gmail.com',
    href: 'mailto:okaforkingsley777@gmail.com',
    icon: Mail,
    label: 'Drop me a line',
  },
  {
    name: 'LinkedIn',
    value: 'linkedin.com/in/kingsley-okafor',
    href: 'https://www.linkedin.com/in/kingsley-okafor-9b150127b/',
    icon: Linkedin,
    label: 'Connect with me',
  },
];

const BlurReveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20, filter: 'blur(8px)' }}
    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const ContactLinks = () => (
  <div className="space-y-3">
    {contactLinks.map((link) => (
      <a
        key={link.name}
        href={link.href}
        target={link.name === 'LinkedIn' ? '_blank' : undefined}
        rel={link.name === 'LinkedIn' ? 'noopener noreferrer' : undefined}
        data-testid={`contact-link-${link.name.toLowerCase()}`}
        className="group flex items-center justify-between px-5 py-4 border border-white/10 bg-black/40 backdrop-blur-sm hover:border-cyan-500/40 hover:bg-black/60 transition-all duration-300"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center border border-cyan-500/25 bg-cyan-500/5 group-hover:bg-cyan-500/12 transition-all">
            <link.icon size={18} className="text-cyan-400" />
          </div>
          <div>
            <div className="text-[10px] text-white/35 tracking-[0.2em] uppercase font-rajdhani">{link.label}</div>
            <div className="text-white/80 font-rajdhani group-hover:text-white transition-colors text-sm">{link.value}</div>
          </div>
        </div>
        <ArrowUpRight size={16} className="text-white/30 group-hover:text-cyan-400 transition-colors" />
      </a>
    ))}
  </div>
);

export default function ContactPage() {
  const sceneRef = useChapterScene('signal-hub');
  return (
    <div ref={sceneRef} data-testid="contact-page">

      {/* ════════════════════════════════════════════════════
          DESKTOP LAYOUT (md and above) — unchanged
      ═════════════════════════════════════════════════════ */}
      <div className="hidden md:block relative h-screen overflow-hidden">

        {/* Full-screen Globe */}
        <div className="absolute inset-0 z-0">
          <Suspense fallback={<GlobeFallback />}>
            <EarthGlobe />
          </Suspense>
        </div>

        {/* Gradient overlays */}
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-black/90 via-black/60 to-black/20 md:from-black/85 md:via-black/45 md:to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-32 z-[1] bg-gradient-to-b from-black/70 to-transparent pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-24 z-[1] bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
        {/* Warm orange bottom glow */}
        <div
          className="absolute inset-x-0 bottom-0 z-[1] pointer-events-none"
          style={{ height: '45%', background: 'radial-gradient(ellipse 90% 55% at 50% 100%, rgba(255,85,0,0.22) 0%, rgba(255,50,0,0.08) 45%, transparent 72%)' }}
        />

        {/* LIVE SIGNAL badge */}
        <div className="absolute top-24 right-8 z-20">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="px-5 py-2.5 font-orbitron text-xs tracking-[0.35em] uppercase flex items-center gap-2.5"
            style={{
              background: 'linear-gradient(135deg, rgba(10,10,10,0.92), rgba(20,20,20,0.88))',
              border: '1px solid rgba(239,68,68,0.2)',
              color: '#ef4444',
              textShadow: '0 0 14px rgba(239,68,68,0.8)',
            }}
          >
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" style={{ boxShadow: '0 0 8px rgba(239,68,68,0.8)' }} />
            LIVE SIGNAL
          </motion.div>
        </div>

        {/* Main content — left overlay */}
        <div className="relative z-10 h-full flex flex-col justify-center pt-20 pb-20 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <div className="max-w-lg space-y-6 sm:space-y-8">
              <BlurReveal delay={0.2}>
                <h1 className="font-headline text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95]" data-testid="contact-title">
                  The architecture<br />
                  is <span style={{ background: 'linear-gradient(90deg, #00F0FF, #3B82F6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    ready.
                  </span>
                </h1>
              </BlurReveal>
              <BlurReveal delay={0.3}>
                <p className="text-lg text-white/45 font-rajdhani leading-relaxed">
                  If you're building something that needs to work at scale — not just
                  ship fast, but survive contact with reality — let's design the system together.
                </p>
              </BlurReveal>
              <BlurReveal delay={0.4}><ContactLinks /></BlurReveal>
              <BlurReveal delay={0.5}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-white/35 font-rajdhani text-sm">
                    <MapPin size={14} />
                    <span>Based in Canada · Available Globally</span>
                  </div>
                  <a
                    href="mailto:okaforkingsley777@gmail.com"
                    data-testid="send-message-btn"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-cyan-500 text-black font-rajdhani font-bold tracking-wider uppercase text-sm hover:bg-cyan-400 transition-colors btn-magnetic cyber-clip"
                  >
                    Let's Talk <ArrowUpRight size={15} />
                  </a>
                </div>
              </BlurReveal>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="absolute bottom-0 left-0 right-0 z-20 py-4 border-t border-white/5" data-testid="footer">
          <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/25 font-rajdhani">
            <span>© 2025 Kingsley Okafor</span>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="tracking-wider uppercase">Available for opportunities</span>
            </div>
            <span>Built with React &amp; Three.js</span>
          </div>
        </footer>
      </div>

      {/* ════════════════════════════════════════════════════
          MOBILE LAYOUT (below md) — circular globe + content
      ═════════════════════════════════════════════════════ */}
      <div className="md:hidden min-h-screen flex flex-col bg-black">

        {/* Globe area — circular with warm glow */}
        <div
          className="relative flex justify-center items-end pt-20 pb-0"
          style={{
            background: 'radial-gradient(ellipse 120% 55% at 50% 100%, rgba(255,65,0,0.32) 0%, rgba(180,40,0,0.10) 45%, #000000 70%)',
            minHeight: '72vw',
          }}
        >
          {/* Circular clipped globe */}
          <motion.div
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            style={{
              width: '92vw',
              height: '92vw',
              borderRadius: '50%',
              overflow: 'hidden',
              position: 'relative',
              boxShadow: '0 0 60px rgba(0,150,255,0.12), 0 0 100px rgba(255,70,0,0.10)',
            }}
          >
            <Suspense fallback={<GlobeFallback />}>
              <EarthGlobe isMobile={true} />
            </Suspense>
            {/* Inner bottom fade to blend into page */}
            <div
              style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'radial-gradient(ellipse 100% 40% at 50% 100%, rgba(0,0,0,0.5) 0%, transparent 60%)',
                pointerEvents: 'none',
              }}
            />
          </motion.div>

          {/* Warm mist below the globe circle */}
          <div
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: '30px',
              background: 'linear-gradient(to top, #000000, transparent)',
              pointerEvents: 'none',
            }}
          />
        </div>

        {/* Contact content section */}
        <div className="relative z-10 px-5 pt-7 pb-10 space-y-5 bg-black">

          {/* LIVE SIGNAL inline */}
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 font-orbitron text-[10px] tracking-[0.3em] uppercase"
            style={{
              background: 'rgba(10,10,10,0.9)',
              border: '1px solid rgba(239,68,68,0.25)',
              color: '#ef4444',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" style={{ boxShadow: '0 0 6px rgba(239,68,68,0.8)' }} />
            LIVE SIGNAL
          </motion.div>

          <BlurReveal delay={0.2}>
            <h1
              className="font-headline text-4xl font-black tracking-tighter uppercase leading-[0.95]"
              data-testid="contact-title"
            >
              The architecture<br />
              is{' '}
              <span style={{ background: 'linear-gradient(90deg, #00F0FF, #3B82F6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                ready.
              </span>
            </h1>
          </BlurReveal>

          <BlurReveal delay={0.3}>
            <p className="text-base text-white/45 font-rajdhani leading-relaxed">
              If you're building something that needs to work at scale — not just
              ship fast, but survive contact with reality — let's design the system together.
            </p>
          </BlurReveal>

          <BlurReveal delay={0.4}><ContactLinks /></BlurReveal>

          <BlurReveal delay={0.5}>
            <div className="flex items-center gap-2 text-white/35 font-rajdhani text-sm">
              <MapPin size={13} />
              <span>Based in Canada · Available Globally</span>
            </div>
          </BlurReveal>

          <BlurReveal delay={0.6}>
            <a
              href="mailto:okaforkingsley777@gmail.com"
              data-testid="send-message-btn"
              className="flex items-center justify-center gap-2 w-full py-4 bg-cyan-500 text-black font-rajdhani font-bold tracking-wider uppercase text-sm hover:bg-cyan-400 transition-colors cyber-clip"
            >
              Let's Talk <ArrowUpRight size={15} />
            </a>
          </BlurReveal>
        </div>

        {/* Footer */}
        <footer className="py-4 border-t border-white/5 bg-black" data-testid="footer-mobile">
          <div className="px-5 flex flex-col items-center gap-1.5 text-xs text-white/20 font-rajdhani text-center">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="tracking-wider uppercase">Available for opportunities</span>
            </div>
            <span>© 2025 Kingsley Okafor · React &amp; Three.js</span>
          </div>
        </footer>
      </div>

    </div>
  );
}
